import type { ReactNode } from 'react';
import { Icon } from '@/components/icons';
import { cx } from './cx';

interface C1OptionProps {
  title: ReactNode;
  description?: ReactNode;
  /** Leading mark: an avatar, a coloured dot, or a glyph. */
  media?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onSelect: () => void;
  /**
   * Renders as a radio when the options are one exclusive choice, which is what
   * a screen reader needs to announce "2 of 5" rather than a list of buttons.
   */
  role?: 'radio' | 'button';
  'aria-label'?: string;
  className?: string;
}

/** One choice in a list of choices: a switchable seat, a scope, an institution. */
export function C1Option({
  title,
  description,
  media,
  selected = false,
  disabled = false,
  onSelect,
  role = 'radio',
  className,
  ...props
}: C1OptionProps) {
  return (
    <button
      type="button"
      className={cx('c1-option', selected && 'c1-option--selected', className)}
      onClick={onSelect}
      disabled={disabled}
      role={role}
      aria-checked={role === 'radio' ? selected : undefined}
      aria-pressed={role === 'button' ? selected : undefined}
      {...props}
    >
      {media ? (
        <span className="c1-optionMedia" aria-hidden="true">
          {media}
        </span>
      ) : null}
      <span className="c1-optionText">
        <span className="c1-optionTitle">{title}</span>
        {description ? <span className="c1-optionDescription">{description}</span> : null}
      </span>
      {/* The tick, not just the tinted background: the selected state must survive
          a reader who cannot separate the two shades. */}
      {selected ? (
        <span className="c1-optionCheck" aria-hidden="true">
          <Icon name="checkmark" size={16} />
        </span>
      ) : null}
    </button>
  );
}
